import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useState,
} from "react";
import { notificationService } from "../../services/api/notification.service";
import { useAuth } from "@/hooks/useAuth";

// Initial state
const initialState = {
  notifications: [],
  unreadCount: 0,
  categorizedCounts: {}, // New: counts by category/sourceModel
  detailedUnreadCount: {}, // New: detailed unread counts
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    pages: 1,
  },
  loading: false,
  error: null,
  filters: {
    type: null,
    isRead: null,
  },
};

// Action types
const actions = {
  FETCH_START: "FETCH_START",
  FETCH_SUCCESS: "FETCH_SUCCESS",
  FETCH_ERROR: "FETCH_ERROR",
  SET_UNREAD_COUNT: "SET_UNREAD_COUNT",
  SET_CATEGORIZED_COUNTS: "SET_CATEGORIZED_COUNTS",
  SET_DETAILED_UNREAD_COUNT: "SET_DETAILED_UNREAD_COUNT",
  MARK_AS_READ: "MARK_AS_READ",
  UPDATE_FILTERS: "UPDATE_FILTERS",
  SET_PAGE: "SET_PAGE",
  RESET_STATE: "RESET_STATE",
};

// Reducer function
const notificationReducer = (state, action) => {
  switch (action.type) {
    case actions.FETCH_START:
      return { ...state, loading: true, error: null };
    case actions.FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        notifications: action.payload.notifications,
        pagination: action.payload.pagination,
        error: null,
      };
    case actions.FETCH_ERROR:
      return { ...state, loading: false, error: action.payload };
    case actions.SET_UNREAD_COUNT:
      return { ...state, unreadCount: action.payload };
    case actions.SET_CATEGORIZED_COUNTS:
      return { ...state, categorizedCounts: action.payload };
    case actions.SET_DETAILED_UNREAD_COUNT:
      return { ...state, detailedUnreadCount: action.payload };
    case actions.MARK_AS_READ:
      return {
        ...state,
        notifications: state.notifications.map((notification) =>
          action.payload.includes(notification._id)
            ? { ...notification, isRead: true }
            : notification
        ),
        unreadCount: Math.max(0, state.unreadCount - action.payload.length),
      };
    case actions.UPDATE_FILTERS:
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
        pagination: { ...state.pagination, page: 1 },
      };
    case actions.SET_PAGE:
      return {
        ...state,
        pagination: { ...state.pagination, page: action.payload },
      };
    case actions.RESET_STATE:
      return initialState;
    default:
      return state;
  }
};

// Create context
const NotificationContext = createContext();

// Provider component
export const NotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const { isAuthenticated, user } = useAuth();

  const [pollingInterval, setPollingInterval] = useState(null);

  // Reset state when auth status changes
  useEffect(() => {
    if (!isAuthenticated) {
      dispatch({ type: actions.RESET_STATE });
      stopPolling();
    }
  }, [isAuthenticated]);

  // Fetch notifications based on current filters and pagination
  const fetchNotifications = async () => {
    if (!isAuthenticated) return;

    try {
      dispatch({ type: actions.FETCH_START });

      const { page, limit } = state.pagination;
      const { type, isRead } = state.filters;

      const query = new URLSearchParams();
      query.append("page", page);
      query.append("limit", limit);

      if (type) query.append("type", type);
      if (isRead !== null) query.append("isRead", isRead);

      const response = await notificationService.getUserNotifications(
        query.toString()
      );

      dispatch({
        type: actions.FETCH_SUCCESS,
        payload: {
          notifications: response.data,
          pagination: response.pagination,
        },
      });
    } catch (error) {
      dispatch({ type: actions.FETCH_ERROR, payload: error.message });
    }
  };
  const fetchadminNotifications = async () => {
    if (!isAuthenticated) return;

    try {
      dispatch({ type: actions.FETCH_START });

      const { page, limit } = state.pagination;
      const { type, isRead } = state.filters;

      const query = new URLSearchParams();
      query.append("page", page);
      query.append("limit", limit);

      if (type) query.append("type", type);
      if (isRead !== null) query.append("isRead", isRead);

      const queryString = query.toString();


      const response = await notificationService.getAdminNotifications(
        queryString
      );


      dispatch({
        type: actions.FETCH_SUCCESS,
        payload: {
          notifications: response.data,
          pagination: response.pagination,
        },
      });
    } catch (error) {
      console.error("Fetch Error:", error.message);
      dispatch({ type: actions.FETCH_ERROR, payload: error.message });
    }
  };

  // Get unread notification count
  const fetchUnreadCount = async () => {
    if (!isAuthenticated) return;

    try {
      const response = await notificationService.getUnreadCount();
      dispatch({
        type: actions.SET_UNREAD_COUNT,
        payload: response.data.count,
      });
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  };

  // NEW: Get categorized counts
  const fetchCategorizedCounts = async () => {
    if (!isAuthenticated) return;

    try {
      const response = await notificationService.getCategorizedCounts();
      dispatch({
        type: actions.SET_CATEGORIZED_COUNTS,
        payload: response.data,
      });
    } catch (error) {
      console.error("Failed to fetch categorized counts:", error);
    }
  };

  // NEW: Get detailed unread count with category breakdown
  const fetchDetailedUnreadCount = async () => {
    if (!isAuthenticated) return;

    try {
      const response = await notificationService.getDetailedUnreadCount();
      dispatch({
        type: actions.SET_DETAILED_UNREAD_COUNT,
        payload: response.data,
      });
    } catch (error) {
      console.error("Failed to fetch detailed unread count:", error);
    }
  };

  // Mark notifications as read
  const markAsRead = async (notificationIds = []) => {
    if (!isAuthenticated) return;

    try {
      await notificationService.markAsRead({ notificationIds });
      dispatch({ type: actions.MARK_AS_READ, payload: notificationIds });

      // Refresh counts
      fetchUnreadCount();
      fetchCategorizedCounts();
      fetchDetailedUnreadCount();
    } catch (error) {
      console.error("Failed to mark notifications as read:", error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!isAuthenticated) return;

    try {
      await notificationService.markAsRead({});

      const allIds = state.notifications.map(
        (notification) => notification._id
      );
      dispatch({ type: actions.MARK_AS_READ, payload: allIds });

      dispatch({ type: actions.SET_UNREAD_COUNT, payload: 0 });

      // Refresh counts
      fetchCategorizedCounts();
      fetchDetailedUnreadCount();
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  // NEW: Mark category as read
  const markCategoryAsRead = async (sourceModel) => {
    if (!isAuthenticated) return;

    try {
      await notificationService.markCategoryAsRead(sourceModel);

      // Refresh all counts
      fetchUnreadCount();
      fetchCategorizedCounts();
      fetchDetailedUnreadCount();
      fetchNotifications();
      fetchadminNotifications();
    } catch (error) {
      console.error("Failed to mark category as read:", error);
    }
  };

  // Delete a notification
  const deleteNotification = async (id) => {
    if (!isAuthenticated) return;

    try {
      await notificationService.deleteNotification(id);
      fetchNotifications();
      fetchadminNotifications();
      fetchUnreadCount();
      fetchCategorizedCounts();
      fetchDetailedUnreadCount();
    } catch (error) {
      console.error("Failed to delete notification:", error);
    }
  };

  // Update filters
  const updateFilters = (filters) => {
    dispatch({ type: actions.UPDATE_FILTERS, payload: filters });
  };

  // Set page
  const setPage = (page) => {
    dispatch({ type: actions.SET_PAGE, payload: page });
  };

  // Start polling for new notifications
  const startPolling = (interval = 60000) => {
    if (pollingInterval) clearInterval(pollingInterval);

    const intervalId = setInterval(() => {
      if (isAuthenticated) {
        fetchUnreadCount();
        fetchCategorizedCounts();
        fetchDetailedUnreadCount();
      } else {
        stopPolling();
      }
    }, interval);

    setPollingInterval(intervalId);
  };

  // Stop polling
  const stopPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  // Initial fetch when component mounts and user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      if (user?.role === "admin") {
        fetchadminNotifications();
      } else {
        fetchNotifications();
      }
      fetchCategorizedCounts();
      fetchDetailedUnreadCount();
      startPolling();

      return () => stopPolling();
    }
  }, [isAuthenticated, user?.role]);

  // Fetch when filters or pagination changes
  useEffect(() => {
    if (isAuthenticated) {
      if (user?.role === "admin") {
        fetchadminNotifications();
      } else {
        fetchNotifications();
      }
    }
  }, [state.filters, state.pagination.page, isAuthenticated, user?.role]);

  // Check for admin status
  const isAdmin = user?.role === "admin";

  // Helper function to get count for specific category
  const getCategoryCount = (sourceModel, unreadOnly = false) => {
    if (unreadOnly) {
      return state.detailedUnreadCount[sourceModel] || 0;
    }
    return state.categorizedCounts[sourceModel] || 0;
  };

  // Context value
  const value = {
    notifications: state.notifications,
    unreadCount: state.unreadCount,
    categorizedCounts: state.categorizedCounts,
    detailedUnreadCount: state.detailedUnreadCount,
    pagination: state.pagination,
    loading: state.loading,
    error: state.error,
    filters: state.filters,
    fetchNotifications,
    fetchadminNotifications,
    fetchUnreadCount,
    fetchCategorizedCounts,
    fetchDetailedUnreadCount,
    markAsRead,
    markAllAsRead,
    markCategoryAsRead,
    deleteNotification,
    updateFilters,
    setPage,
    getCategoryCount,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook for using the notification context
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};
